import axios from 'axios';
import { getIdToken } from '@/pages/MyAccount/components/firebaseIdToken';

// Same wording as the other token-gated screens; kept local so this file does not import from the Super Admin customer-health
// module (cypress/node-tests/customer-health-panel.test.js pins who may import that one).
const NO_TOKEN_MESSAGE = "Couldn't confirm your sign-in. Reload the page and try again.";

/**
 * My Account → Billing — the two calls of teamio-serverless-api/myaccount-service (design:
 * docs/plans/2026-10-14-billing-tab-design.md):
 *
 *   GET  /billing-portal?siteID=…   the read-only summary: plan line items, next charge, the card on file (+ expiry status), invoices.
 *   POST /billing-portal            opens Stripe's hosted portal for ONE page: { flow: 'payment_method_update' | 'customer_update' }.
 *
 * (The summary is the GET of the same path on purpose — the shared API Gateway has no room for a new one.)
 *
 * THE SERVER IS THE GATE: it verifies this Firebase ID token and checks the caller is an Admin of THIS site. Like api/outreach.js,
 * every call FAILS CLOSED — with no token it does not call at all. Only 'payment_method_update' and 'customer_update' exist;
 * there is deliberately no cancel / plan-change flow (phase 1), and openBillingPortal() rejects anything else BEFORE any request.
 */

export const BILLING_TIMEOUT_MS = 30000;
export const PORTAL_FLOWS = ['payment_method_update', 'customer_update'];

function noToken() {
  const err = new Error(NO_TOKEN_MESSAGE);
  err.noToken = true;
  return err;
}

/** The Billing tab is where the practice lands after the portal. */
export function billingReturnUrl(origin) {
  return `${origin || window.location.origin}/app/my-account?tab=billing`;
}

export async function fetchBillingSummary({ auth, siteID } = {}) {
  const idToken = await getIdToken(auth);
  if (!idToken) throw noToken();
  const { data } = await axios.get('/billing-portal', {
    params: { siteID },
    headers: { Authorization: `Bearer ${idToken}` },
    timeout: BILLING_TIMEOUT_MS,
    crossdomain: true,
  });
  return data;
}

/** Resolves to the Stripe URL to send the browser to. `flow` null/undefined = the portal's home page. */
export async function openBillingPortal({ auth, siteID, flow, returnUrl } = {}) {
  if (flow && !PORTAL_FLOWS.includes(flow)) return Promise.reject(new Error('Unknown billing action.'));
  const idToken = await getIdToken(auth);
  if (!idToken) throw noToken();
  const { data } = await axios.post('/billing-portal', { siteID, flow: flow || undefined, returnUrl: returnUrl || billingReturnUrl() }, {
    headers: { Authorization: `Bearer ${idToken}` },
    timeout: BILLING_TIMEOUT_MS,
    crossdomain: true,
  });
  if (!data || !data.url) throw new Error('Could not open the billing page. Please try again.');
  return data.url;
}
